import Header from "@/components/navigation/Header";
import Footer from "@/components/sections/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Award, Shield, TrendingUp, Building2, BookOpen, Target, CheckCircle } from "lucide-react";
const About = () => {
  const stats = [{
    label: "Years of Experience",
    value: "10+",
    icon: Award
  }, {
    label: "Clients Served",
    value: "500+",
    icon: Users
  }, {
    label: "Bitcoin Secured",
    value: "$50M+",
    icon: Shield
  }, {
    label: "Success Rate",
    value: "98%",
    icon: TrendingUp
  }];
  const values = [{
    title: "Security First",
    description: "We prioritize the security of your Bitcoin holdings with institutional-grade custody solutions and best practices.",
    icon: Shield
  }, {
    title: "Education Focused",
    description: "We believe in empowering our clients through comprehensive education and transparent communication.",
    icon: BookOpen
  }, {
    title: "Real Estate Expertise",
    description: "Our team understands the unique needs and challenges of real estate investors and professionals.",
    icon: Building2
  }, {
    title: "Strategic Approach",
    description: "We develop customized Bitcoin strategies that align with your specific real estate investment goals.",
    icon: Target
  }];
  const team = [{
    name: "Michael Rodriguez",
    title: "Chief Bitcoin Officer & Founder",
    bio: "Former real estate investment executive with 15+ years in the industry. Early Bitcoin adopter since 2012 with deep expertise in institutional Bitcoin strategies.",
    specialties: ["Investment Strategy", "Institutional Bitcoin", "Real Estate Finance"]
  }, {
    name: "Sarah Chen",
    title: "Head of Security & Custody",
    bio: "Cybersecurity expert specializing in Bitcoin custody solutions. Former security engineer at major cryptocurrency exchanges.",
    specialties: ["Bitcoin Security", "Custody Solutions", "Risk Management"]
  }, {
    name: "David Thompson",
    title: "Director of Financial Services",
    bio: "CPA with extensive experience in cryptocurrency accounting and tax compliance for real estate investment firms.",
    specialties: ["Bitcoin Accounting", "Tax Compliance", "Financial Reporting"]
  }];
  return <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white py-[50px]">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About Chief Bitcoin Officer
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              We are the leading Bitcoin advisory firm specialized in serving real estate 
              investors and professionals who want to integrate Bitcoin into their investment strategies.
            </p>
          </div>
        </div>
      </section>

      <main className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stats Section */}
          <section className="mb-20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return <div key={index} className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-bitcoin-orange/10 rounded-full mb-4">
                      <IconComponent className="h-8 w-8 text-bitcoin-orange" />
                    </div>
                    <div className="text-3xl font-bold text-foreground mb-2">
                      {stat.value}
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {stat.label}
                    </div>
                  </div>;
            })}
            </div>
          </section>

          {/* Mission Section */}
          <section className="mb-20">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-foreground mb-8">Our Mission</h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                To bridge the gap between traditional real estate investing and the digital asset revolution. 
                We believe that Bitcoin represents a generational opportunity for real estate professionals 
                to diversify their portfolios and hedge against inflation while maintaining their core 
                investment expertise.
              </p>
              <div className="bg-muted rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  Why Real Estate Investors Need Bitcoin Expertise
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-bitcoin-orange mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground">Portfolio Diversification</h4>
                      <p className="text-sm text-muted-foreground">Bitcoin provides uncorrelated returns to traditional real estate assets</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-bitcoin-orange mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground">Inflation Hedge</h4>
                      <p className="text-sm text-muted-foreground">Digital scarcity complements real estate's physical scarcity</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-bitcoin-orange mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground">Liquidity Enhancement</h4>
                      <p className="text-sm text-muted-foreground">24/7 trading provides liquidity between real estate transactions</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-bitcoin-orange mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground">Future-Proofing</h4>
                      <p className="text-sm text-muted-foreground">Prepare for the evolving financial landscape</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Values Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Our Values</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                These principles guide everything we do and ensure we deliver the highest 
                quality service to our real estate investment clients.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {values.map((value, index) => {
              const IconComponent = value.icon;
              return <Card key={index} className="hover:shadow-soft transition-smooth">
                    <CardHeader>
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="p-2 bg-bitcoin-orange/10 rounded-lg">
                          <IconComponent className="h-6 w-6 text-bitcoin-orange" />
                        </div>
                      </div>
                      <CardTitle className="text-xl font-bold">
                        {value.title}
                      </CardTitle>
                      <CardDescription>
                        {value.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>;
            })}
            </div>
          </section>

          {/* Team Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Meet Our Team</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our experienced team combines deep real estate expertise with cutting-edge 
                Bitcoin knowledge to serve our clients' unique needs.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((member, index) => <Card key={index} className="hover:shadow-soft transition-smooth">
                  <CardHeader>
                    <div className="w-24 h-24 bg-gradient-to-br from-primary to-bitcoin-orange rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Users className="h-12 w-12 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-center">
                      {member.name}
                    </CardTitle>
                    <CardDescription className="text-center text-bitcoin-orange font-semibold">
                      {member.title}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                      {member.bio}
                    </p>
                    <div>
                      <h4 className="font-semibold text-foreground text-sm mb-2">Specialties:</h4>
                      <div className="flex flex-wrap gap-1">
                        {member.specialties.map((specialty, specialtyIndex) => <Badge key={specialtyIndex} variant="secondary" className="text-xs">
                            {specialty}
                          </Badge>)}
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-muted rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Work with Bitcoin Experts?
            </h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join hundreds of real estate professionals who trust Chief Bitcoin Officer 
              to guide their Bitcoin investment journey. Let's discuss how we can help 
              you achieve your financial goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-bitcoin-orange text-white px-8 py-3 rounded-lg font-semibold hover:bg-bitcoin-orange/90 transition-colors">
                Schedule a Consultation
              </button>
              <button className="border border-border text-foreground px-8 py-3 rounded-lg font-semibold hover:bg-muted-foreground/10 transition-colors">
                Learn About Our Services
              </button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>;
};
export default About;